import { useState } from "react";
import { Loader2, Phone, Plus } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { useWorkspace } from "@/contexts/WorkspaceContext";
import { useWorkspacePermissions } from "@/hooks/use-workspace-permissions";
import { useToast } from "@/hooks/use-toast";
import { type PhoneNumber, useDeletePhoneNumber, usePhoneNumbers } from "@/hooks/use-phone-numbers";
import AddPhoneNumberDialog from "@/components/PhoneNumbers/AddPhoneNumberDialog";
import EditPhoneNumberDialog from "@/components/PhoneNumbers/EditPhoneNumberDialog";
import PhoneNumberCard from "@/components/PhoneNumbers/PhoneNumberCard";
import InitiateOutboundCallDialog from "@/components/OutboundCall/InitiateOutboundCallDialog";
import {
  AlertDialog,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { refusalOf } from "@/utils/phone-number-rules";

export default function PhoneNumbers() {
  const { currentWorkspaceId } = useWorkspace();
  const { canEdit, canDelete } = useWorkspacePermissions();
  const { data: phoneNumbers, isLoading, isError, refetch } = usePhoneNumbers(currentWorkspaceId || undefined);
  const deletePhoneNumber = useDeletePhoneNumber();
  const { toast } = useToast();

  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showOutboundCallDialog, setShowOutboundCallDialog] = useState(false);
  const [editing, setEditing] = useState<PhoneNumber | null>(null);
  const [deleting, setDeleting] = useState<PhoneNumber | null>(null);
  const [deleteError, setDeleteError] = useState<string>();
  const isDeleting = deletePhoneNumber.isPending;

  const confirmDelete = (phoneNumber: PhoneNumber) => {
    setDeleteError(undefined);
    setDeleting(phoneNumber);
  };

  const handleDelete = async () => {
    setDeleteError(undefined);
    try {
      await deletePhoneNumber.mutateAsync(deleting!);
      toast({
        title: "Phone number deleted",
        description: `${deleting!.phone_number} was deleted. Its campaigns and call history are kept.`,
      });
      setDeleting(null);
    } catch (failure) {
      // A campaign that can still dial from the number refuses it (409) — the
      // server says which, and what to do.
      setDeleteError(refusalOf(failure, {}, "The phone number could not be deleted.").message);
    }
  };

  const addButton = (label: string, testId: string) =>
    canEdit && (
      <Button onClick={() => setShowAddDialog(true)} data-testid={testId}>
        <Plus className="w-4 h-4 mr-2" />
        {label}
      </Button>
    );

  return (
    <div className="h-full overflow-y-auto p-8">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-semibold mb-1">Phone Numbers</h1>
          <p className="text-sm text-muted-foreground">
            Manage phone numbers and assign them to AI agents for inbound/outbound calls
          </p>
        </div>
        <div className="flex gap-2">
          {canEdit && (
            <Button variant="outline" onClick={() => setShowOutboundCallDialog(true)}>
              <Phone className="w-4 h-4 mr-2" />
              Make Call
            </Button>
          )}
          {addButton("Add Number", "phone-add")}
        </div>
      </div>

      {isLoading ? (
        <div className="grid gap-4" role="status" aria-label="Loading phone numbers">
          <Skeleton className="h-40 w-full" />
          <Skeleton className="h-40 w-full" />
        </div>
      ) : isError ? (
        <div role="alert" className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-border rounded-lg">
          <p className="text-muted-foreground mb-4">The phone numbers could not be loaded.</p>
          {/* Trying again puts the placeholders back until it answers. */}
          <Button variant="outline" onClick={() => refetch()}>
            Try again
          </Button>
        </div>
      ) : phoneNumbers?.length ? (
        <div className="grid gap-4" data-testid="phone-list">
          {phoneNumbers.map((phone) => (
            <PhoneNumberCard
              key={phone.id}
              phoneNumber={phone}
              onEdit={setEditing}
              onDelete={confirmDelete}
              canEdit={canEdit}
              canDelete={canDelete}
            />
          ))}
        </div>
      ) : (
        <div className="flex flex-col items-center justify-center h-96 border-2 border-dashed border-border rounded-lg">
          <p className="text-muted-foreground mb-4">No phone numbers yet</p>
          {addButton("Add Your First Number", "phone-add-first")}
        </div>
      )}

      {currentWorkspaceId && (
        <>
          <AddPhoneNumberDialog
            open={showAddDialog}
            onClose={() => setShowAddDialog(false)}
            workspaceId={currentWorkspaceId}
          />
          <InitiateOutboundCallDialog
            open={showOutboundCallDialog}
            onOpenChange={setShowOutboundCallDialog}
            workspaceId={currentWorkspaceId}
          />
        </>
      )}
      {editing && (
        <EditPhoneNumberDialog key={editing.id} phoneNumber={editing} onClose={() => setEditing(null)} />
      )}

      {/* A delete in flight finishes in view; a refusal stays on screen with it. */}
      <AlertDialog open={!!deleting} onOpenChange={() => !isDeleting && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {deleting?.phone_number}?</AlertDialogTitle>
            <AlertDialogDescription>
              The number is removed from Verbeo. Campaigns that used it and your call history are kept; they
              just no longer show a number. A campaign that can still dial from it has to be cancelled or
              finish first.
            </AlertDialogDescription>
          </AlertDialogHeader>
          {deleteError && (
            <p role="alert" data-testid="phone-delete-error" className="text-sm text-destructive">
              {deleteError}
            </p>
          )}
          <AlertDialogFooter>
            <AlertDialogCancel disabled={isDeleting}>Cancel</AlertDialogCancel>
            <Button
              variant="destructive"
              onClick={handleDelete}
              disabled={isDeleting}
              aria-busy={isDeleting}
              data-testid="phone-delete-confirm"
            >
              {isDeleting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isDeleting ? "Deleting…" : "Delete"}
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
